import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container", "title", "subtitle",
    "signInForm", "signUpForm",
    "email", "password", "error", "errorText", "submitBtn", "submitText", "spinner", "successIcon",
    "signupEmail", "signupUsername", "signupPassword", "signupError", "signupErrorText", 
    "signupSubmitBtn", "signupSubmitText", "signupSpinner", "signupSuccessIcon"
  ]
  
  static values = {
    signInUrl: String,
    signUpUrl: String
  }
  
  connect() {
    this.pendingAction = null
    this.pendingUrl = null
    this.pendingMethod = null
  }
  
  // Open modal with pending action
  open(action, url, method = 'POST') {
    this.pendingAction = action
    this.pendingUrl = url
    this.pendingMethod = method
    
    // Update title based on action
    const actionTitles = {
      'like': 'Sign in to like this post',
      'bookmark': 'Sign in to bookmark this',
      'comment': 'Sign in to comment',
      'follow': 'Sign in to follow',
      'vote': 'Sign in to vote',
      'default': 'Sign in to continue'
    }
    
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = actionTitles[action] || actionTitles['default']
    }
    
    // Show modal with animation
    this.element.classList.remove('hidden')
    requestAnimationFrame(() => {
      this.element.classList.add('active')
    })
    
    // Focus email input
    setTimeout(() => {
      if (this.hasEmailTarget) {
        this.emailTarget.focus()
      }
    }, 300)
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden'
  }
  
  close() {
    this.element.classList.remove('active')
    
    setTimeout(() => {
      this.element.classList.add('hidden')
      this.resetForms()
    }, 300)
    
    // Restore body scroll
    document.body.style.overflow = ''
  }
  
  backdropClick(event) {
    if (event.target === this.element) {
      this.close()
    }
  }
  
  showSignIn() {
    if (this.hasSignUpFormTarget) this.signUpFormTarget.classList.add('hidden')
    if (this.hasSignInFormTarget) this.signInFormTarget.classList.remove('hidden')
    if (this.hasTitleTarget) this.titleTarget.textContent = 'Sign in to continue'
    if (this.hasSubtitleTarget) this.subtitleTarget.textContent = 'Join the premier motorcoach community'
  }
  
  showSignUp() {
    if (this.hasSignInFormTarget) this.signInFormTarget.classList.add('hidden')
    if (this.hasSignUpFormTarget) this.signUpFormTarget.classList.remove('hidden')
    if (this.hasTitleTarget) this.titleTarget.textContent = 'Create your account'
    if (this.hasSubtitleTarget) this.subtitleTarget.textContent = 'Join thousands of motorcoach enthusiasts'
  }
  
  async submitSignIn(event) {
    event.preventDefault()
    
    const email = this.emailTarget.value
    const password = this.passwordTarget.value
    
    // Show loading state
    this.setLoading(true)
    this.hideError()
    
    try {
      const response = await fetch(this.signInUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ user: { email, password } })
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.showSuccess()
        
        // Server-side deferred intents (auth redirects) return a destination after replay.
        if (data.redirect_to) {
          setTimeout(() => {
            window.location.href = data.redirect_to
          }, 800)
          return
        }

        // Execute pending action after short delay
        setTimeout(() => {
          this.executePendingAction()
        }, 800)
      } else {
        this.showError(data.error || 'Invalid email or password')
        this.setLoading(false)
      }
    } catch (error) {
      this.showError('Something went wrong. Please try again.')
      this.setLoading(false)
    }
  }
  
  async submitSignUp(event) {
    event.preventDefault()
    
    const email = this.signupEmailTarget.value
    const username = this.signupUsernameTarget.value
    const password = this.signupPasswordTarget.value
    
    // Show loading state
    this.setSignupLoading(true)
    this.hideSignupError()
    
    try {
      const response = await fetch(this.signUpUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ user: { email, username, password } })
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.showSignupSuccess()
        
        if (data.redirect_to) {
          setTimeout(() => {
            window.location.href = data.redirect_to
          }, 800)
          return
        }

        // Execute pending action after short delay
        setTimeout(() => {
          this.executePendingAction()
        }, 800)
      } else {
        // Signup is consolidated onto one verified path. If the server hands
        // us a redirect, follow it rather than showing a dead-end error.
        if (data.redirect_to) {
          window.location.href = data.redirect_to
          return
        }
        this.showSignupError(data.error || 'Could not create account')
        this.setSignupLoading(false)
      }
    } catch (error) {
      this.showSignupError('Something went wrong. Please try again.')
      this.setSignupLoading(false)
    }
  }
  
  async executePendingAction() {
    if (this.pendingUrl) {
      try {
        const response = await fetch(this.pendingUrl, {
          method: this.pendingMethod,
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
            'Accept': 'application/json, text/vnd.turbo-stream.html'
          }
        })
        
        // If it's a Turbo Stream response, let Turbo handle it
        if (response.headers.get('Content-Type')?.includes('turbo-stream')) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        }
        
        // Close modal
        this.close()
        
        // Show success toast
        this.showToast('Action completed!')
        
      } catch (error) {
        console.error('Failed to execute pending action:', error)
        // Still close modal and reload
        window.location.reload()
      }
    } else {
      // No pending action, just reload
      window.location.reload()
    }
  }
  
  setLoading(loading) {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = loading
      if (loading) {
        this.submitBtnTarget.classList.add('loading')
      } else {
        this.submitBtnTarget.classList.remove('loading')
      }
    }
    if (this.hasSubmitTextTarget) this.submitTextTarget.classList.toggle('hidden', loading)
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle('hidden', !loading)
  }
  
  setSignupLoading(loading) {
    if (this.hasSignupSubmitBtnTarget) {
      this.signupSubmitBtnTarget.disabled = loading
      if (loading) {
        this.signupSubmitBtnTarget.classList.add('loading')
      } else {
        this.signupSubmitBtnTarget.classList.remove('loading')
      }
    }
    if (this.hasSignupSubmitTextTarget) this.signupSubmitTextTarget.classList.toggle('hidden', loading)
    if (this.hasSignupSpinnerTarget) this.signupSpinnerTarget.classList.toggle('hidden', !loading)
  }
  
  showSuccess() {
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.classList.add('success')
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add('hidden')
    if (this.hasSuccessIconTarget) this.successIconTarget.classList.remove('hidden')
  }
  
  showSignupSuccess() {
    if (this.hasSignupSubmitBtnTarget) this.signupSubmitBtnTarget.classList.add('success')
    if (this.hasSignupSpinnerTarget) this.signupSpinnerTarget.classList.add('hidden')
    if (this.hasSignupSuccessIconTarget) this.signupSuccessIconTarget.classList.remove('hidden')
  }
  
  showError(message) {
    if (this.hasErrorTarget) this.errorTarget.classList.remove('hidden')
    if (this.hasErrorTextTarget) this.errorTextTarget.textContent = message
  }
  
  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden')
  }
  
  showSignupError(message) {
    if (this.hasSignupErrorTarget) this.signupErrorTarget.classList.remove('hidden')
    if (this.hasSignupErrorTextTarget) this.signupErrorTextTarget.textContent = message
  }
  
  hideSignupError() {
    if (this.hasSignupErrorTarget) this.signupErrorTarget.classList.add('hidden')
  }
  
  resetForms() {
    if (this.hasEmailTarget) this.emailTarget.value = ''
    if (this.hasPasswordTarget) this.passwordTarget.value = ''
    if (this.hasSignupEmailTarget) this.signupEmailTarget.value = ''
    if (this.hasSignupUsernameTarget) this.signupUsernameTarget.value = ''
    if (this.hasSignupPasswordTarget) this.signupPasswordTarget.value = ''
    
    this.hideError()
    this.hideSignupError()
    this.setLoading(false)
    this.setSignupLoading(false)
    
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.classList.remove('success')
    if (this.hasSignupSubmitBtnTarget) this.signupSubmitBtnTarget.classList.remove('success')
    if (this.hasSubmitTextTarget) this.submitTextTarget.classList.remove('hidden')
    if (this.hasSignupSubmitTextTarget) this.signupSubmitTextTarget.classList.remove('hidden')
    if (this.hasSuccessIconTarget) this.successIconTarget.classList.add('hidden')
    if (this.hasSignupSuccessIconTarget) this.signupSuccessIconTarget.classList.add('hidden')
    
    this.showSignIn()
  }
  
  showToast(message) {
    const toast = document.createElement('div')
    toast.className = 'fixed bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 bg-green-600 text-white rounded-lg shadow-lg z-50 animate-fade-in'
    toast.textContent = message
    document.body.appendChild(toast)
    
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }
}

// Global function to open auth modal
window.openAuthModal = function(action, url, method = 'POST') {
  const modal = document.getElementById('auth-modal')
  if (modal) {
    const controller = window.Stimulus.getControllerForElementAndIdentifier(modal, 'auth-modal')
    if (controller) {
      controller.open(action, url, method)
    }
  }
}
